import {
  Box,
  Button,
  Center,
  Divider,
  Heading,
  Input,
  SimpleGrid,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import ErrorText from "components/API/ErrorText";
import BoxShadowContainer from "components/UIComponents/BoxShadowContainer";
import LoaderSpinner from "components/UIComponents/LoaderSpinner";
import { RequestMethod, RequestName } from "hooks/api/apiHookTypes";
import useApiRequest from "hooks/api/useApiHook";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import PayoutForm from "./PayoutForm";
import PayoutHistory from "./PayoutHistory";
import TransferCard from "./TransferCard";
import { buildReferralLink, formatUsd } from "./helpers";

const ProfileReferals = () => {
  const key = useSelector((state: RootState) => state.apiKey.apiKey);
  const toast = useToast();

  // Bumped after a transfer/payout to force stats + payout-list re-fetches
  // (instead of waiting for the poll tick).
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const { data, loading, error } = useApiRequest(
    {
      apiToken: key,
      method: RequestMethod.GET,
      name: RequestName.GET_INVITER_STATS,
    },
    60000,
    refreshKey
  );

  // Account details carry the numeric users.id used to build the referral link.
  const { data: accountData } = useApiRequest({
    apiToken: key,
    method: RequestMethod.GET,
    name: RequestName.GET_ACCOUNT_DETAILS,
  });

  const stats = data?.data;
  const available = Math.max(0, Number(stats?.ready_to_withdraw ?? 0));
  const referralLink = buildReferralLink(accountData?.data?.user_data?.user_id);

  const onCopy = () => {
    if (!referralLink) return;
    // navigator.clipboard is undefined in insecure contexts / old browsers —
    // guard before calling .then, or it throws a TypeError.
    if (!navigator.clipboard) {
      toast({ title: "Couldn't copy the link", status: "error", duration: 2000 });
      return;
    }
    navigator.clipboard.writeText(referralLink).then(
      () =>
        toast({
          title: "Referral link copied",
          status: "success",
          duration: 2000,
        }),
      () =>
        toast({
          title: "Couldn't copy the link",
          status: "error",
          duration: 2000,
        })
    );
  };

  if (loading) {
    return (
      <Center>
        <LoaderSpinner />
      </Center>
    );
  }

  if (error) {
    return <ErrorText />;
  }

  return (
    <Stack spacing={5}>
      <BoxShadowContainer>
        <Box>
          <Heading as="h2" size="md">
            Affiliate program
          </Heading>
          <Text mt={2} color="text.secondary">
            Earn 10% of every purchase made by users you invite.
          </Text>
        </Box>
      </BoxShadowContainer>

      <BoxShadowContainer>
        <Stack spacing={3} width="100%">
          <Heading as="h3" size="sm">
            Your referral link
          </Heading>
          {referralLink ? (
            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={3}
              align={{ base: "stretch", md: "center" }}
            >
              <Input
                value={referralLink}
                isReadOnly
                fontSize={15}
                flex="1"
                onFocus={(e) => e.target.select()}
              />
              <Button colorScheme="green" onClick={onCopy} flexShrink={0}>
                Copy
              </Button>
            </Stack>
          ) : (
            <Text color="text.secondary">Your referral link is loading…</Text>
          )}
        </Stack>
      </BoxShadowContainer>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <BoxShadowContainer>
          <Box>
            <Text color="text.secondary">Invited users</Text>
            <Heading as="p" size="lg" mt={1}>
              {Number(stats?.invited_users_total ?? 0)}
            </Heading>
          </Box>
        </BoxShadowContainer>
        <BoxShadowContainer>
          <Box>
            <Text color="text.secondary">Total earned</Text>
            <Heading as="p" size="lg" mt={1}>
              {formatUsd(stats?.total_earned)} $
            </Heading>
          </Box>
        </BoxShadowContainer>
        <BoxShadowContainer>
          <Box>
            <Text color="text.secondary">Referral balance</Text>
            <Heading as="p" size="lg" mt={1}>
              {formatUsd(stats?.ready_to_withdraw)} $
            </Heading>
          </Box>
        </BoxShadowContainer>
      </SimpleGrid>

      <Divider />

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <TransferCard
          apiToken={key}
          available={available}
          onTransferred={refresh}
        />
        <PayoutForm apiToken={key} available={available} onRequested={refresh} />
      </SimpleGrid>

      <PayoutHistory apiToken={key} refreshKey={refreshKey} />
    </Stack>
  );
};

export default ProfileReferals;
